import * as React from "react";
import { ArrowUp, LoaderCircle } from "lucide-react";
import { ZOT_EMPTY_SHORTCUTS, ZOT_SLASH_COMMANDS, matchesFor } from "@/lib/zot-commands";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

/**
 * The one Zot composer. A single text field and a send button: no mode, repository, cap, or
 * model pickers. Typing `/` opens the command menu (arrow keys, Enter or Tab to pick, Esc to
 * close); on an empty chat the common commands appear as shortcuts under the box. Shared by
 * the Zot page and the drawer so both behave identically.
 */
export function ZotComposer({
  value,
  onChange,
  onSubmit,
  disabled = false,
  busy = false,
  showShortcuts = false,
  autoFocus = false,
  id = "zot-message",
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  busy?: boolean;
  showShortcuts?: boolean;
  autoFocus?: boolean;
  id?: string;
}) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const matches = matchesFor(value);
  const open = menuOpen && matches.length > 0;
  const active = Math.min(activeIndex, Math.max(matches.length - 1, 0));

  function handleChange(next: string) {
    setActiveIndex(0);
    onChange(next);
    setMenuOpen(next.startsWith("/"));
  }

  function pick(command: string) {
    onChange(`${command} `);
    setMenuOpen(false);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (open) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((current) => {
          const step = event.key === "ArrowDown" ? 1 : -1;
          return (current + step + matches.length) % matches.length;
        });
        return;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        pick(matches[active]!);
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setMenuOpen(false);
        return;
      }
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
    }
    if (event.key === "Escape") setMenuOpen(false);
  }

  return (
    <div>
      <div className="relative">
        <label className="sr-only" htmlFor={id}>
          Message Zot
        </label>
        <Textarea
          id={id}
          value={value}
          maxLength={4000}
          onChange={(event) => handleChange(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message Zot, or type / for commands"
          disabled={disabled || busy}
          autoFocus={autoFocus}
          aria-expanded={open}
          aria-controls={open ? "zot-slash-commands" : undefined}
          aria-activedescendant={open ? `zot-command-${matches[active]}` : undefined}
          className="min-h-20 resize-none"
        />
        {open ? (
          <ul
            id="zot-slash-commands"
            role="listbox"
            aria-label="Commands"
            className="absolute inset-x-0 bottom-full z-10 mb-1 max-h-60 overflow-y-auto rounded-lg border bg-popover p-1 shadow-md"
          >
            {matches.map((command, index) => {
              const entry = ZOT_SLASH_COMMANDS.find((item) => item.command === command)!;
              const highlighted = index === active;
              return (
                <li
                  key={command}
                  id={`zot-command-${command}`}
                  role="option"
                  aria-selected={highlighted}
                >
                  <button
                    type="button"
                    tabIndex={-1}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      pick(command);
                    }}
                    onMouseMove={() => setActiveIndex(index)}
                    className={cn(
                      "flex w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left text-sm",
                      highlighted && "bg-accent",
                    )}
                  >
                    <span className="font-medium">{command}</span>
                    <span className="text-xs text-muted-foreground">{entry.purpose}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          Zot picks the model, repository, and spend cap for you. Nothing runs until you confirm.
        </span>
        <Button
          type="button"
          size="sm"
          disabled={disabled || busy || !value.trim()}
          onClick={onSubmit}
        >
          {busy ? (
            <LoaderCircle className="mr-2 size-4 animate-spin" aria-hidden />
          ) : (
            <ArrowUp className="mr-2 size-4" aria-hidden />
          )}
          Ask Zot
        </Button>
      </div>
      {showShortcuts ? (
        <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Command shortcuts">
          {ZOT_EMPTY_SHORTCUTS.map((command) => (
            <button
              key={command}
              type="button"
              disabled={disabled || busy}
              onClick={() => pick(command)}
              className="inline-flex min-h-11 items-center rounded-full border px-2.5 py-1 text-xs text-muted-foreground hover:bg-accent hover:text-foreground md:min-h-0"
            >
              {command}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
